import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigatorSearchField from '../services/digital-media/miniapp/components/discovery/NavigatorSearchField';
import { useDiscoverySession } from './context/DiscoverySessionContext';
import type { StaffRolesMap } from './context/StaffRolesContext';

interface ServiceDashboardProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
}

const SERVICE_ROUTES: Record<string, string> = {
  events: '/events/',
  'digital-media': '/digital-media/',
};

function openExternal(url: string): void {
  if (window.Telegram?.WebApp?.openLink) window.Telegram.WebApp.openLink(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

export default function ServiceDashboard({
  activeServices,
  staffRoles,
  servicesLoading,
  servicesError,
}: ServiceDashboardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { bootstrap, bootstrapLoading, setQuery, setFilters } = useDiscoverySession();
  const requestedService = searchParams.get('service')?.trim().toLowerCase() ?? '';
  const hasDigitalMedia = activeServices.includes('digital-media');
  useEffect(() => {
    if (!requestedService || servicesLoading || servicesError) return;
    const route = SERVICE_ROUTES[requestedService];
    if (route && activeServices.includes(requestedService)) navigate(route, { replace: true });
  }, [activeServices, navigate, requestedService, servicesError, servicesLoading]);
  function search(query: string) {
    if (!hasDigitalMedia) return;
    if (!bootstrap?.flags.navigatorEnabled || !bootstrap.flags.navigatorSearchV2Enabled) {
      navigate('/digital-media/');
      return;
    }
    setQuery(query);
    setFilters({ themeIds: [], typeIds: [], stateId: null });
    navigate('/digital-media/search');
  }
  const practiceIds =
    bootstrap?.types
      .filter((tag) => ['meditation', 'practice', 'tuning'].includes(tag.stableId))
      .map((tag) => tag.stableId) ?? [];
  const permanentResources = bootstrap?.resources.slice(0, 3) ?? [];

  return (
    <div className="dm-discovery-page dm-root-home">
      <main>
        <header className="dm-home-hero">
          <p className="dm-eyebrow">Материалы Кати Колобаевой</p>
          <h1>Что для тебя важно сейчас?</h1>
          <p>Найди точные фрагменты, голосовые, медитации и практики.</p>
        </header>
        <NavigatorSearchField onSearch={search} />
        {servicesLoading || bootstrapLoading ? (
          <div className="dm-discovery-status">Загружаем направления…</div>
        ) : null}
        {servicesError ? (
          <div className="dm-discovery-status" role="alert">
            {servicesError}
          </div>
        ) : null}
        {hasDigitalMedia &&
        bootstrap?.flags.navigatorEnabled &&
        bootstrap.flags.navigatorHomeEnabled &&
        bootstrap.flags.navigatorSearchV2Enabled ? (
          <>
            <button
              type="button"
              className="dm-home-now"
              onClick={() => navigate('/digital-media/now')}
            >
              <span className="material-symbols-outlined">wb_twilight</span>
              <span>
                <small>Свежие материалы</small>
                <strong>Что сейчас</strong>
              </span>
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
            <section>
              <div className="dm-section-heading">
                <div>
                  <p className="dm-eyebrow">Быстрый вход</p>
                  <h2>Темы</h2>
                </div>
                <button type="button" onClick={() => navigate('/digital-media/topics')}>
                  Все
                </button>
              </div>
              <div className="dm-home-topic-row">
                {bootstrap.topics.core.slice(0, 4).map((topic) => (
                  <button
                    key={topic.stableId}
                    type="button"
                    onClick={() => navigate(`/digital-media/topics/${topic.stableId}`)}
                  >
                    {topic.title}
                  </button>
                ))}
              </div>
            </section>
            <div className="dm-home-entry-grid">
              <button type="button" onClick={() => navigate('/digital-media/states')}>
                <span className="material-symbols-outlined">favorite</span>
                <strong>По состоянию</strong>
                <small>Если трудно назвать тему</small>
              </button>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setFilters({ themeIds: [], typeIds: practiceIds, stateId: null });
                  navigate(`/digital-media/search?typeIds=${practiceIds.join(',')}`);
                }}
              >
                <span className="material-symbols-outlined">self_improvement</span>
                <strong>Медитации и практики</strong>
                <small>Каталог форматов</small>
              </button>
            </div>
          </>
        ) : null}
        <section>
          <p className="dm-eyebrow">Ещё</p>
          <div className="dm-home-link-list">
            <button
              type="button"
              disabled={!hasDigitalMedia}
              onClick={() => navigate('/digital-media/')}
            >
              <span className="material-symbols-outlined">library_books</span>
              <span>Материалы</span>
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button type="button" onClick={() => navigate('/events/')}>
              <span className="material-symbols-outlined">event_available</span>
              <span>События и ретриты</span>
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            {bootstrap?.flags.navigatorEnabled && bootstrap.flags.navigatorConsultationsEnabled ? (
              <button type="button" onClick={() => navigate('/digital-media/consultations')}>
                <span className="material-symbols-outlined">forum</span>
                <span>Консультации</span>
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            ) : null}
            {permanentResources.map((resource) => (
              <button
                key={resource.slug}
                type="button"
                onClick={() => openExternal(resource.canonicalUrl)}
              >
                <span className="material-symbols-outlined">bookmark</span>
                <span>{resource.title}</span>
                <span className="material-symbols-outlined">open_in_new</span>
              </button>
            ))}
          </div>
        </section>
        {Object.keys(staffRoles).length ? (
          <section className="dm-staff-tools">
            <p className="dm-eyebrow">Инструменты персонала</p>
            {staffRoles.events?.isManager ? (
              <button type="button" onClick={() => navigate('/events/manager/')}>
                <span className="material-symbols-outlined">calendar_month</span>Менеджер событий
              </button>
            ) : null}
          </section>
        ) : null}
      </main>
    </div>
  );
}
